// TimeSettingPage.js
import React, { useEffect, useState } from "react";
import { getDatabase, ref, get, update } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function TimeSettingPage() {
  const [userUID, setUserUID] = useState(null);
  const [devices, setDevices] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [visibleCols, setVisibleCols] = useState({ userName: false, userAddress: false, group: false });
  const [bulkStart, setBulkStart] = useState("08:00");
  const [bulkEnd, setBulkEnd] = useState("16:00");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const toggleCol = (col) => {
    setVisibleCols((prev) => ({ ...prev, [col]: !prev[col] }));
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserUID(user.uid);
      } else {
        setUserUID(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!userUID) return;

    const db = getDatabase();
    const userMapRef = ref(db, `user_esp32_map/${userUID}`);
    const indexRef = ref(db, `esp32_index`);

    Promise.all([get(userMapRef), get(indexRef)]).then(([userSnap, indexSnap]) => {
      if (userSnap.exists() && indexSnap.exists()) {
        const userData = userSnap.val();
        const indexData = indexSnap.val();

        const shortIDs = Object.keys(userData);
        const validIDs = shortIDs.filter((id) => indexData[id]);
        setDevices(validIDs);

        const permissionPromises = validIDs.map((id) => {
          const group = indexData[id];
          return get(ref(db, `esp32_members/${group}/${id}`)).then((snap) => {
            const data = snap.val();
            return {
              id,
              group,
              userName: data?.userName || "-",
              userAddress: data?.userAddress || "-",
              start: data?.canSendTime?.start || "00:00",
              end: data?.canSendTime?.end || "23:59"
            };
          });
        });

        Promise.all(permissionPromises).then((results) => {
          const perms = {};
          results.forEach(({ id, group, userName, userAddress, start, end }) => {
            perms[id] = { group, userName, userAddress, start, end };
          });
          setPermissions(perms);
          setLoading(false);
        });
      }
    });
  }, [userUID]);   


const filteredDevices = devices.filter((id) => {
  const term = searchTerm.toLowerCase();
  const item = permissions[id];
  if (!item) return false;

  // รวมข้อมูลทั้งหมดที่อาจแสดงออกมาเป็น string ชุดเดียว
  const content = [
    id,
    visibleCols.userName ? item.userName : "",
    visibleCols.userAddress ? item.userAddress : "",
    visibleCols.group ? item.group : "",
    item.start,
    item.end,
    item.start.replace(":", ""),
    item.end.replace(":", ""),
    item.start.split(":")[0],
    item.end.split(":")[0],
  ].join(" ").toLowerCase();

  return content.includes(term);
});

const paginatedDevices = filteredDevices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleSelect = (id) => {
    setSelectedDevices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const applyBulkTime = () => {
    const db = getDatabase();
    const updates = {};
    selectedDevices.forEach((id) => {
      const group = permissions[id]?.group;
      if (group) {
        updates[`esp32_members/${group}/${id}/canSendTime/start`] = bulkStart;
        updates[`esp32_members/${group}/${id}/canSendTime/end`] = bulkEnd;
        permissions[id].start = bulkStart;
        permissions[id].end = bulkEnd;
      }
    });
    update(ref(db), updates);
    setPermissions({ ...permissions });
  };

  if (loading) return <div className="container mt-5">Loading...</div>;

  return (
    <div className="container mt-5">
      <div className="card shadow-lg">
        <div className="card-body">
          <h4 className="mb-3">กำหนดเวลา รับการแจ้งเตือน (ใช้สำหรับเวลาเลิกงาน)</h4>

          <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
            <div className="form-check form-check-inline">
              <input type="checkbox" className="form-check-input" id="chk-name" checked={visibleCols.userName} onChange={() => toggleCol('userName')} />
              <label className="form-check-label" htmlFor="chk-name">ชื่อผู้ใช้</label>
            </div>
            <div className="form-check form-check-inline">
              <input type="checkbox" className="form-check-input" id="chk-addr" checked={visibleCols.userAddress} onChange={() => toggleCol('userAddress')} />
              <label className="form-check-label" htmlFor="chk-addr">ที่อยู่</label>
            </div>
            <div className="form-check form-check-inline">
              <input type="checkbox" className="form-check-input" id="chk-group" checked={visibleCols.group} onChange={() => toggleCol('group')} />
              <label className="form-check-label" htmlFor="chk-group">กลุ่ม</label>
            </div>
            <input type="text" className="form-control ms-auto" style={{ maxWidth: "300px" }} placeholder="ค้นหา..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>

          <div className="d-flex gap-2 align-items-center mb-3">
            <label>ตั้งเวลาทำงาน:</label>
            <input type="time" className="form-control w-auto" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} />
            <input type="time" className="form-control w-auto" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)} />
            <button className="btn btn-primary" onClick={applyBulkTime} disabled={selectedDevices.length === 0}>บันทึกเวลา</button>
            <span className="text-muted">เลือกแล้ว {selectedDevices.length} รายการ</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table table-bordered table-striped" style={{ minWidth: '100%', whiteSpace: 'nowrap' }}>
              <thead className="table-dark">
                <tr>
                  <th><input type="checkbox" onChange={(e) => {
                    if (e.target.checked) setSelectedDevices(filteredDevices);
                    else setSelectedDevices([]);
                  }} /></th>
                  <th>Device ID</th>
                  {visibleCols.userName && <th>ชื่อผู้ใช้</th>}
                  {visibleCols.userAddress && <th>ที่อยู่</th>}
                  {visibleCols.group && <th>กลุ่ม</th>}
                  <th>เวลาเริ่ม</th>
                  <th>เวลาสิ้นสุด</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDevices.map((id) => (
                  <tr key={id}>
                    <td><input type="checkbox" checked={selectedDevices.includes(id)} onChange={() => toggleSelect(id)} /></td>
                    <td>{id}</td>
                    {visibleCols.userName && <td>{permissions[id]?.userName}</td>}
                    {visibleCols.userAddress && <td>{permissions[id]?.userAddress}</td>}
                    {visibleCols.group && <td>{permissions[id]?.group}</td>}
                    <td>{permissions[id]?.start}</td>
                    <td>{permissions[id]?.end}</td>
                  </tr>
                ))}
                {filteredDevices.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">ไม่มีข้อมูล</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-center my-3">
                <button
                  className="btn btn-outline-dark me-2"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >ย้อนกลับ</button>
                <span className="align-self-center">หน้า {currentPage} / {Math.ceil(filteredDevices.length / itemsPerPage)}</span>
                <button
                  className="btn btn-outline-dark ms-2"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredDevices.length / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(filteredDevices.length / itemsPerPage)}
                >ถัดไป</button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default TimeSettingPage;
